/**
 * Reporte de Incautos y Cargos — migración web de ares5095, ampliada.
 *
 * Lista los incautos (tabla `increg`) o los cargos (tabla `llamadas`) de un
 * rango de fechas, con filtro opcional por sucursal y empresa, y separa lo
 * facturado de lo cobrado con dos ejes de fecha excluyentes (mismo criterio
 * que el informe de cobros de matrículas):
 *   - Facturado: el rango filtra por fecha de REGISTRO del documento. Cada
 *     fila trae su estado (Vigente / Pagado / Cancelado) y, si ya se cobró,
 *     la fecha y el recibo del cobro. Responde cuánto se facturó y cuánto
 *     de eso ya entró.
 *   - Cobrado: el rango filtra por fecha en que ENTRÓ el dinero y solo lista
 *     los pagados. Es lo que suma el cuadre de caja por este concepto.
 * Los cancelados quedan en 0 y no suman. Exporta a Excel e imprime.
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi, usePerfilSucursal, useSucursales } from "lib/api";
import { useMultiempresa, SelectorEmpresa, nombreEmpresa } from "lib/multiempresa";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const etiquetaEstado = (estado) => {
  if (estado === "C") return "Cancelado";
  if (estado === "P") return "Pagado";
  return "Vigente";
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const TITULO = { cargo: "Reporte de Cargos", incauto: "Reporte de Incautos" };

const AVISO = {
  facturado:
    "El rango filtra por fecha de registro del documento. Facturado es lo registrado " +
    "(sin los cancelados); Cobrado, lo que de eso ya pagó el cliente; Pendiente, lo vigente.",
  cobrado:
    "El rango filtra por fecha en que entró el dinero y solo lista los documentos pagados. " +
    "Es lo que suma el cuadre de caja por este concepto.",
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin documentos en el rango seleccionado
      </Typography>
    </Stack>
  );
}

const ESTILO_ESTADO = {
  C: { bgcolor: "#FDECEA", color: "#C62828" },
  P: { bgcolor: "#E3F2FD", color: "#1565C0" },
  V: { bgcolor: "#E8F5E9", color: "#2E7D32" },
};

function EstadoChip({ estado }) {
  const estilo = ESTILO_ESTADO[estado] || ESTILO_ESTADO.V;
  return <Chip size="small" label={etiquetaEstado(estado)} sx={{ ...estilo, fontWeight: 700 }} />;
}

// Columnas por tipo y modo. Una sola definición alimenta la grilla, el Excel
// y la impresión: en "facturado" la fila es un documento registrado (con su
// estado y su cobro si lo hubo); en "cobrado" es dinero que entró (fecha del
// pago y recibo primero).
function definirColumnas(tipo, modo, multiempresa) {
  const empresa = multiempresa.activo
    ? [
        {
          field: "empresa",
          h: "Empresa",
          w: 130,
          get: (r) => nombreEmpresa(multiempresa.empresas, r.empresa || 1),
        },
      ]
    : [];
  const documento =
    tipo === "incauto"
      ? [
          { field: "chasis", h: "Chasis", flex: 1, minWidth: 180, get: (r) => r.chasis || "" },
          {
            field: "vehiculo",
            h: "Vehículo",
            w: 170,
            get: (r) => [r.marca, r.modelo, r.colore].filter(Boolean).join(" "),
          },
        ]
      : [
          {
            field: "descripcion",
            h: "Descripción",
            flex: 1,
            minWidth: 200,
            get: (r) => r.descripcion || "",
          },
        ];
  const cliente = { field: "nombres", h: "Cliente", flex: 1, minWidth: 170, get: (r) => r.nombres };
  const prestamo = { field: "noprest", h: "Préstamo", w: 95, get: (r) => r.noprest };
  const sucursal = { field: "sucursal", h: "Suc.", w: 75, get: (r) => r.sucursal };
  const usuario = { field: "user", h: "Usuario", w: 120, get: (r) => r.user };
  const valor = {
    field: "valor",
    h: modo === "cobrado" ? "Monto" : "Valor",
    w: 115,
    right: true,
    get: (r) => fmtMoney(r.valor),
  };
  const recibo = { field: "recibopago", h: "Recibo", w: 85, get: (r) => r.recibopago || "—" };
  if (modo === "cobrado") {
    return [
      { field: "fechacobro", h: "Fecha cobro", w: 105, get: (r) => r.fechacobro },
      recibo,
      prestamo,
      ...empresa,
      cliente,
      ...documento,
      { field: "fecha", h: "Registrado", w: 105, get: (r) => r.fecha },
      valor,
      sucursal,
      usuario,
    ];
  }
  return [
    prestamo,
    { field: "fecha", h: "Fecha", w: 105, get: (r) => r.fecha },
    ...empresa,
    cliente,
    ...documento,
    valor,
    { field: "estado", h: "Estado", w: 110, get: (r) => etiquetaEstado(r.estado), chip: true },
    { field: "fechacobro", h: "Cobrado el", w: 105, get: (r) => r.fechacobro || "—" },
    recibo,
    sucursal,
    usuario,
  ];
}

export default function InformeCargos({ embedded = false, onExit, tipoInicial = "cargo" } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { esadmin, sucursalPropia } = usePerfilSucursal();
  const sucursales = useSucursales();
  // Multi-empresa: filtro y columna (solo con el modo activo)
  const multiempresa = useMultiempresa();
  const [empresaFiltro, setEmpresaFiltro] = useState("0");

  const [tipo, setTipo] = useState(tipoInicial === "incauto" ? "incauto" : "cargo");
  // Eje de fecha: "facturado" (registro) o "cobrado" (entrada del dinero).
  // Son preguntas distintas; mezclarlas en una tabla es lo que confundía.
  const [modo, setModo] = useState("facturado");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [sucursal, setSucursal] = useState("");
  const [rows, setRows] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(false);

  // Un usuario que no es admin queda fijado a su sucursal.
  useEffect(() => {
    if (!esadmin && sucursalPropia) setSucursal(sucursalPropia);
  }, [esadmin, sucursalPropia]);

  const cargar = async (t = tipo, m = modo) => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/informecargos", {
        tipo: t,
        modo: m,
        fechadesde: fecha1,
        fechahasta: fecha2,
        sucursal: sucursal.trim(),
        ...(multiempresa.activo ? { empresa: Number(empresaFiltro) } : {}),
      });
      if (ok) {
        setRows((data.documentos || []).map((r, idx) => ({ ...r, id: r.id ?? idx })));
        setTotales(data.totales || null);
      } else {
        setRows([]);
        setTotales(null);
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambiarTipo = (t) => {
    setTipo(t);
    setRows([]);
    setTotales(null);
    cargar(t, modo);
  };
  const cambiarModo = (m) => {
    if (!m || m === modo) return;
    setModo(m);
    // Cambia la pregunta: los resultados anteriores ya no aplican.
    setRows([]);
    setTotales(null);
    cargar(tipo, m);
  };

  const esCobrado = modo === "cobrado";
  const cols = definirColumnas(tipo, modo, multiempresa);
  const titulo = TITULO[tipo];
  const sufijoSucursal = sucursal.trim() ? ` · Sucursal ${sucursal.trim()}` : "";
  const subtitulo = `${esCobrado ? "Cobrados" : "Registrados"} del ${fechaDMY(
    fecha1
  )} al ${fechaDMY(fecha2)}${sufijoSucursal}`;
  const totalFacturado = totales?.facturado ?? 0;
  const totalCobrado = esCobrado ? totales?.cobradoperiodo ?? 0 : totales?.cobrado ?? 0;
  const totalPendiente = totales?.pendiente ?? 0;
  const cancelados = totales?.cancelados ?? 0;
  // Filas de total del Excel/impresión: la etiqueta va a la izquierda de la
  // columna de valor y el monto debajo de ella.
  const idxValor = cols.findIndex((c) => c.field === "valor");
  const filaTotal = (etiqueta, monto) =>
    cols.map((c, i) => {
      if (i === idxValor - 1) return etiqueta;
      if (i === idxValor) return monto;
      return "";
    });

  const exportarExcel = () => {
    if (!rows.length) {
      showAlert({ message: "No hay datos para exportar", color: "warning" });
      return;
    }
    const aoa = [
      [titulo],
      [subtitulo],
      [],
      cols.map((c) => c.h),
      ...rows.map((r) => cols.map((c) => (c.field === "valor" ? r.valor : c.get(r)))),
      [],
    ];
    if (esCobrado) {
      aoa.push(filaTotal("COBRADO:", totalCobrado));
    } else {
      aoa.push(filaTotal("FACTURADO:", totalFacturado));
      aoa.push(filaTotal("COBRADO:", totalCobrado));
      aoa.push(filaTotal("PENDIENTE:", totalPendiente));
    }
    const hoja = XLSX.utils.aoa_to_sheet(aoa);
    hoja["!cols"] = cols.map((c) => ({
      wch: c.flex ? 32 : Math.max(10, Math.round((c.w || 100) / 8)),
    }));
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, esCobrado ? "Cobrado" : "Facturado");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
    const prefijo = tipo === "incauto" ? "ReporteIncautos" : "ReporteCargos";
    XLSX.writeFile(libro, `${prefijo}_${stamp}.xlsx`);
  };

  const imprimir = () => {
    if (!rows.length) {
      showAlert({ message: "No hay datos para imprimir", color: "warning" });
      return;
    }
    imprimirListadoGrande({
      titulo: `${titulo} — ${esCobrado ? "Cobrado" : "Facturado"}`,
      subtitulo,
      cabeceras: cols.map((c) => c.h),
      filas: rows.map((r) => cols.map((c) => c.get(r))),
      totales: filaTotal(
        esCobrado ? "COBRADO:" : "FACTURADO:",
        fmtMoney(esCobrado ? totalCobrado : totalFacturado)
      ),
    });
  };

  const columns = cols.map((c) => ({
    field: c.field,
    headerName: c.h,
    ...(c.flex ? { flex: c.flex, minWidth: c.minWidth } : { width: c.w }),
    ...(c.right ? { align: "right", headerAlign: "right" } : {}),
    valueGetter: (_value, row) => c.get(row),
    ...(c.chip ? { renderCell: (params) => <EstadoChip estado={params.row.estado} /> } : {}),
  }));

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Reporte de Incautos y Cargos"
        subtitle="Facturado (por fecha de registro) o cobrado (por fecha en que entró el dinero), con export e impresión"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportarExcel}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Exportar Excel
            </Button>
          </Stack>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={tipo}
                onChange={(e) => cambiarTipo(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="incauto">Incautos</MenuItem>
                <MenuItem value="cargo">Cargos</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                disabled={!esadmin}
                sx={fpInputSx}
              >
                <MenuItem value="">Todas</MenuItem>
                {sucursales.map((s) => (
                  <MenuItem key={s.label} value={String(s.label).split("-")[0]}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {multiempresa.activo && (
              <Grid item xs={6} sm={3} md={2}>
                <SelectorEmpresa
                  value={empresaFiltro}
                  onChange={setEmpresaFiltro}
                  empresas={multiempresa.empresas}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={() => cargar()}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack
            direction="row"
            spacing={1.2}
            flexWrap="wrap"
            alignItems="center"
            sx={{ mb: 1 }}
            useFlexGap
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={modo}
              onChange={(_e, v) => cambiarModo(v)}
              sx={{ bgcolor: "#FFFFFF" }}
            >
              <ToggleButton value="facturado" sx={{ ...btnBase, px: 1.6, py: 0.4 }}>
                Facturado
              </ToggleButton>
              <ToggleButton value="cobrado" sx={{ ...btnBase, px: 1.6, py: 0.4 }}>
                Cobrado
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography
              sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
            >
              {rows.length} {tipo === "incauto" ? "incautos" : "cargos"}
              {!esCobrado && cancelados > 0 ? ` · ${cancelados} cancelados` : ""}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {esCobrado ? (
              <ResumenChip
                label="COBRADO EN EL PERÍODO"
                value={`RD$ ${fmtMoney(totalCobrado)}`}
                color="#2E7D32"
              />
            ) : (
              <>
                <ResumenChip label="FACTURADO" value={`RD$ ${fmtMoney(totalFacturado)}`} />
                <ResumenChip
                  label="COBRADO"
                  value={`RD$ ${fmtMoney(totalCobrado)}`}
                  color="#2E7D32"
                />
                <ResumenChip
                  label="PENDIENTE"
                  value={`RD$ ${fmtMoney(totalPendiente)}`}
                  color="#EF6C00"
                />
              </>
            )}
          </Stack>
          <Typography sx={{ mb: 1.6, fontSize: "0.82rem", color: FP.color.textSecondary }}>
            {AVISO[modo]}
          </Typography>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
